import { Controller } from "@hotwired/stimulus"

// Auto-scrolling vertical marquee for the hero gift list.
// Duplicates list items for a seamless infinite loop, then drives
// the scroll via requestAnimationFrame so hover-pause and
// prefers-reduced-motion work reliably without CSS animation quirks.
export default class extends Controller {
  static targets = ["viewport", "track"]
  static values  = { duration: { type: Number, default: 22 } }

  connect() {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return

    this.#duplicateItems()
    this.#startScroll()
    this.#bindHoverPause()
  }

  disconnect() {
    if (this.#animationFrameId) cancelAnimationFrame(this.#animationFrameId)
  }

  // ── private ──────────────────────────────────────────────────────────────

  #animationFrameId = null
  #paused           = false
  #offset           = 0
  #halfHeight       = null
  #lastTime         = null

  #duplicateItems() {
    const track = this.trackTarget
    const originals = Array.from(track.children)
    originals.forEach(node => track.appendChild(node.cloneNode(true)))
  }

  #halfHeightPx() {
    if (!this.#halfHeight) {
      this.#halfHeight = this.trackTarget.scrollHeight / 2
    }
    return this.#halfHeight
  }

  #startScroll() {
    const tick = (timestamp) => {
      if (!this.#lastTime) this.#lastTime = timestamp

      if (!this.#paused) {
        const elapsedSeconds = (timestamp - this.#lastTime) / 1000
        const halfHeight     = this.#halfHeightPx()
        // px per second = total height of one set / duration
        const pixelsPerSecond = halfHeight / this.durationValue
        this.#offset += pixelsPerSecond * elapsedSeconds

        // Reset offset for seamless loop
        if (this.#offset >= halfHeight) {
          this.#offset -= halfHeight
        }

        this.trackTarget.style.transform = `translateY(-${this.#offset}px)`
      }

      this.#lastTime = timestamp
      this.#animationFrameId = requestAnimationFrame(tick)
    }

    this.#animationFrameId = requestAnimationFrame(tick)
  }

  #bindHoverPause() {
    this.element.addEventListener("mouseenter", () => { this.#paused = true })
    this.element.addEventListener("mouseleave", () => { this.#paused = false })
  }
}
